import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PortService } from 'src/app/services/port/port.service';
import { PortModalService } from 'src/app/services/port-modal/port-modal.service';
import { PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-ports',
  templateUrl: './ports.component.html',
  styleUrls: ['./ports.component.scss']
})
export class PortsComponent extends DestructorComponent implements OnInit {

  // Expose Math to template
  Math = Math;

  // Data properties
  ports: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  currentPage: number = 1;
  searchTerm: string = '';
  filters: Record<string, any> = {};

  // Checkbox state tracking
  originalPortStates: Map<string, {show_as_from: boolean, display: boolean}> = new Map();
  hasChanges: boolean = false;
  isSaving: boolean = false;
  changedRows: any[] = [];

  constructor(
    private _portService: PortService,
    private _portModalService: PortModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadPorts();
  }

  loadPorts(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    this.subscriptions.push(
      this._portService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.ports = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;

            // Store original states for change tracking
            this.storeOriginalStates();
          } else {
            this.ports = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading ports:', error);
          this.ports = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadPorts(page, this.searchTerm, this.filters);
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadPorts(1, this.searchTerm, this.filters);
  }

  openAddPortModal(): void {
    this._portModalService.clearSelectedPort();
    const modalElement = document.getElementById('kt_modal_port_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditPortModal(port: any): void {
    this._portModalService.setSelectedPort(port);
    const modalElement = document.getElementById('kt_modal_port_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deletePort(port: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete port "${port.name || 'this port'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(port);
      }
    });
  }

  private performDelete(port: any): void {
    this.subscriptions.push(
      this._portService.DeleteDataById(port._id || port.id).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Port has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            });
            this.loadPorts(this.currentPage, this.searchTerm, this.filters);
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete port.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error deleting port:', error);

          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  onPortAdded(): void {
    console.log('Port added, refreshing list...');
    this.loadPorts(this.currentPage, this.searchTerm, this.filters);
  }

  onPortUpdated(): void {
    console.log('Port updated, refreshing list...');
    this.loadPorts(this.currentPage, this.searchTerm, this.filters);
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  private storeOriginalStates(): void {
    this.originalPortStates.clear();
    this.ports.forEach(port => {
      this.originalPortStates.set(port._id || port.id, {
        show_as_from: port.show_as_from || false,
        display: port.display || false
      });
    });
    this.hasChanges = false;
    this.changedRows = [];
  }

  onCheckboxChange(port: any, field: 'show_as_from' | 'display', event: any): void {
    const value = event.target.checked;
    port[field] = value;
    this.onFieldChange(port._id || port.id, field, value);
  }

  onFieldChange(portId: string, field: string, value: any): void {
    console.log(`Port ${portId} field ${field} changed to:`, value);

    // Check if this port is already in changed rows
    const existingIndex = this.changedRows.findIndex(row => row.port_id === portId);

    if (existingIndex >= 0) {
      // Update existing changed row
      this.changedRows[existingIndex][field] = value;
    } else {
      // Add new changed row with current values from original state
      const original = this.originalPortStates.get(portId);
      const currentPort = this.ports.find(p => (p._id || p.id) === portId);

      if (original && currentPort) {
        this.changedRows.push({
          port_id: portId,
          show_as_from: currentPort.show_as_from || false,
          display: currentPort.display || false
        });
      }
    }

    this.hasChanges = this.changedRows.length > 0;
  }

  saveChanges(): void {
    if (!this.hasChanges || this.isSaving || this.changedRows.length === 0) {
      return;
    }

    const changedCount = this.changedRows.length;
    console.log('Saving changes for ports:', this.changedRows);

    this.isSaving = true;

    // Call API to save changes
    this.subscriptions.push(
      this._portService.UpdateDataValue({ ports: this.changedRows }).subscribe({
        next: (response: any) => {
          this.isSaving = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `Successfully updated ${changedCount} port(s)!`,
              icon: 'success',
              confirmButtonText: 'OK'
            });

            // Reset the changes after successful save
            this.hasChanges = false;
            this.changedRows = [];

            // Update original states
            this.storeOriginalStates();
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update port settings.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error updating port settings:', error);
          this.isSaving = false;

          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }
}
